// src/components/InlinePdfSpread.jsx
import "../pdfWorkerSetup";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Document, Page } from "react-pdf";
import axios from "axios";

const InlinePdfSpread = forwardRef(function InlinePdfSpread(
  {
    fileUrl,
    className = "",
    viewportHeight = 600,
    onStateChange,
    initialFit = "fitW",
    stackDirection = "horizontal",
    pageGap = 0,
    disableSwipeNav = false,
  },
  ref
) {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [dual, setDual] = useState(true);
  const [baseW, setBaseW] = useState(0);
  const [baseH, setBaseH] = useState(0);
  const [scale, setScale] = useState(1);
  const [fitMode, setFitMode] = useState(initialFit);
  const [wrapW, setWrapW] = useState(0);
  const [scrollPos, setScrollPos] = useState({ x: 0, y: 0 });

  // --- 🚩 State สำหรับการโหลดไฟล์ ---
  const [pdfSource, setPdfSource] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState("idle"); // idle, resolving, downloading, success, error, empty
  const [debugMsg, setDebugMsg] = useState("");

  const wrapRef = useRef(null);
  const scrollerRef = useRef(null);

  // --- 🚩 1. Smart Loader: แก้ปัญหาค้างหน้าโหลด ---
  useEffect(() => {
    let objectUrl = null;
    let isMounted = true;

    const loadPdfAsBlob = async () => {
      setLoadingStatus("resolving");
      setDebugMsg("กำลังค้นหาที่อยู่ไฟล์...");

      // 1. รอรับค่า URL (เผื่อส่งมาเป็น Promise)
      let targetUrl = fileUrl;
      if (targetUrl && typeof targetUrl.then === 'function') {
        try {
          targetUrl = await targetUrl;
        } catch (e) {
          console.error("Promise Error:", e);
          if (isMounted) {
            setLoadingStatus("error");
            setDebugMsg("ไม่สามารถดึงลิงก์ไฟล์ได้");
          }
          return;
        }
      }

      // 2. 🛡️ เช็คว่ามี URL จริงไหม? ถ้าไม่มี ให้หยุดเลย (แก้ปัญหาค้าง)
      console.log("📄 PDF Target URL:", targetUrl); // <--- ดูตรงนี้ใน F12
      
      if (!targetUrl || typeof targetUrl !== 'string') {
        if (isMounted) {
          setLoadingStatus("empty"); // แจ้งว่าไม่มีไฟล์
          setDebugMsg("ไม่พบเอกสาร PDF ในรายการนี้");
        }
        return;
      }

      // 3. ถ้าเป็น Blob URL อยู่แล้ว ให้ใช้เลย
      if (targetUrl.startsWith('blob:')) {
        if (isMounted) {
          setPdfSource(targetUrl);
          setLoadingStatus("success");
        }
        return;
      }

      // 4. โหลดไฟล์ผ่าน Axios
      try {
        if (isMounted) {
          setLoadingStatus("downloading");
          setDebugMsg("กำลังดาวน์โหลดไฟล์ต้นฉบับ...");
        }

        const response = await axios.get(targetUrl, {
          responseType: 'blob',
          timeout: 60000, // เพิ่มเวลาเผื่อไฟล์ใหญ่
        });

        if (response.data.size === 0) {
          throw new Error("File size is 0 bytes");
        }

        if (isMounted) {
          objectUrl = URL.createObjectURL(response.data);
          setPdfSource(objectUrl);
          setLoadingStatus("success");
        }
      } catch (err) {
        console.error("🚩 Blob Download Error:", err);
        // Fallback: ถ้าโหลด Blob ไม่ได้ ลองส่ง URL ตรงๆ ไปให้ Viewer (เผื่อฟลุ๊ค)
        if (isMounted) {
          console.warn("Switching to direct URL fallback...");
          setPdfSource(targetUrl);
          // เราถือว่า success ไปก่อนเพื่อให้ Viewer ลองพยายาม render ดู
          setLoadingStatus("success"); 
        }
      }
    };

    loadPdfAsBlob();

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileUrl]);

  // ... (ส่วน Logic อื่นๆ เหมือนเดิม) ...
  // --- Media query ---
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const upd = () => setDual(mq.matches);
    upd();
    mq.addEventListener("change", upd);
    return () => mq.removeEventListener("change", upd);
  }, []);

  // --- Track wrapper width ---
  useLayoutEffect(() => {
    const ro = new ResizeObserver(([entry]) => setWrapW(entry.contentRect.width));
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // --- Auto fit scale ---
  useLayoutEffect(() => {
    if (!baseW || !baseH) return;
    if (fitMode === "fitW") {
      const oneWidth = dual ? (wrapW - (dual ? pageGap : 0)) / (dual ? 2 : 1) : wrapW;
      const s = oneWidth / baseW;
      setScale(Math.min(5, Math.max(0.1, s)));
    } else if (fitMode === "fitH") {
      const s = viewportHeight / baseH;
      setScale(Math.min(5, Math.max(0.1, s)));
    }
  }, [fitMode, wrapW, viewportHeight, baseW, baseH, dual, pageGap]);

  // --- เลือกหน้าที่จะแสดง ---
  const pages = useMemo(() => {
    if (!numPages) return [];
    if (!dual) return [page];
    if (stackDirection === "vertical") return [page, Math.min(page + 1, numPages)];
    const right = page % 2 === 0 ? page : Math.min(page + 1, numPages);
    const left = Math.max(1, right - 1);
    return [left, right];
  }, [page, numPages, dual, stackDirection]);

  // --- ส่ง control ออกภายนอก ---
  useImperativeHandle(ref, () => ({
    prev: () => setPage((p) => Math.max(1, p - (dual ? 2 : 1))),
    next: () => setPage((p) => Math.min(numPages || p, p + (dual ? 2 : 1))),
    goto: (v) => setPage(() => Math.max(1, Math.min(v, numPages || v))),
    zoomIn: () => { setFitMode("manual"); setScale((s) => Math.min(5, s + 0.15)); },
    zoomOut: () => { setFitMode("manual"); setScale((s) => Math.max(0.1, s - 0.15)); },
    fitWidth: () => setFitMode("fitW"),
    fitHeight: () => setFitMode("fitH"),
    toggleDual: () => setDual((d) => !d),
  }));

  // --- onStateChange callback ---
  useEffect(() => {
    onStateChange?.({ page, numPages, dual, fitMode, scale });
  }, [page, numPages, dual, fitMode, scale, onStateChange]);

  // --- Scroll sync ---
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => setScrollPos({ x: el.scrollLeft, y: el.scrollTop });
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTo(scrollPos.x, scrollPos.y);
  }, [scale]);

  // --- Gesture drag ---
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const drag = { active: false, sx: 0, sy: 0, scrollX: 0, scrollY: 0 };
    const ts = (e) => {
      const t = e.touches?.[0]; if (!t) return;
      drag.active = true; drag.sx = t.clientX; drag.sy = t.clientY;
      drag.scrollX = el.scrollLeft; drag.scrollY = el.scrollTop;
    };
    const tm = (e) => {
      if (!drag.active) return;
      const t = e.touches?.[0]; if (!t) return;
      el.scrollLeft = drag.scrollX - (t.clientX - drag.sx);
      el.scrollTop = drag.scrollY - (t.clientY - drag.sy);
    };
    const te = (e) => {
      if (!drag.active) return;
      drag.active = false;
      if (disableSwipeNav) return;
      const t = e.changedTouches?.[0]; if (!t) return;
      const dx = t.clientX - drag.sx;
      const dy = t.clientY - drag.sy;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) setPage((p) => Math.min(numPages || p, p + (dual ? 2 : 1)));
        else setPage((p) => Math.max(1, p - (dual ? 2 : 1)));
      }
    };
    el.addEventListener("touchstart", ts, { passive: true });
    el.addEventListener("touchmove", tm, { passive: true });
    el.addEventListener("touchend", te, { passive: true });
    const md = (e) => {
      drag.active = true; drag.sx = e.clientX; drag.sy = e.clientY;
      drag.scrollX = el.scrollLeft; drag.scrollY = el.scrollTop;
      el.style.cursor = "grabbing";
    };
    const mm = (e) => {
      if (!drag.active) return;
      el.scrollLeft = drag.scrollX - (e.clientX - drag.sx);
      el.scrollTop = drag.scrollY - (e.clientY - drag.sy);
    };
    const mu = () => { drag.active = false; el.style.cursor = "default"; };
    el.addEventListener("mousedown", md);
    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup", mu);
    return () => {
      el.removeEventListener("touchstart", ts);
      el.removeEventListener("touchmove", tm);
      el.removeEventListener("touchend", te);
      el.removeEventListener("mousedown", md);
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup", mu);
    };
  }, [dual, numPages]);

  // --- คีย์บอร์ดเปลี่ยนหน้า ---
  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowLeft", "PageUp"].includes(e.key)) {
        e.preventDefault(); setPage((p) => Math.max(1, p - (dual ? 2 : 1)));
      }
      if (["ArrowRight", "PageDown", " "].includes(e.key)) {
        e.preventDefault(); setPage((p) => Math.min(numPages || p, p + (dual ? 2 : 1)));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dual, numPages]);

  // --- Render UI (ปรับปรุงใหม่ให้แสดง Error/Empty) ---
  const rowColClass = dual && stackDirection === "vertical" ? "flex-col" : "flex-row";

  // เช็คสถานะการโหลดเพื่อแสดงผล
  const showLoading = ["resolving", "downloading"].includes(loadingStatus);
  const showError = loadingStatus === "error";
  const showEmpty = loadingStatus === "empty";

  return (
    <div ref={wrapRef} className={`relative ${className}`} style={{ height: viewportHeight }}>
      {/* 🚩 1. เพิ่ม flex เข้าไปที่ตัว Scroll */}
      <div ref={scrollerRef} className="w-full h-full overflow-auto overscroll-contain bg-[#f3f3f3] flex">
        
        {/* 🚩 2. เปลี่ยน mx-auto เป็น m-auto เพื่อให้กึ่งกลางทั้งแกน X และ Y */}
        <div className="w-max h-max m-auto pt-4 pb-6">
          
          {/* 1. Loading UI */}
          {showLoading && (
            <div className="flex flex-col items-center justify-center p-10 h-64 gap-3">
               <div className="w-8 h-8 border-4 border-[#7b6c61] border-t-transparent rounded-full animate-spin"></div>
               <div className="text-[#7b6c61] font-medium">{debugMsg}</div>
            </div>
          )}

          {/* 2. Error / Empty UI */}
          {(showError || showEmpty) && (
             <div className="flex flex-col items-center justify-center p-10 h-64 text-red-500">
               <div className="text-4xl mb-2">⚠️</div>
               <div>{debugMsg || "ไม่สามารถแสดงเอกสารได้"}</div>
             </div>
          )}

          {/* 3. Document PDF */}
          {(!showLoading && !showError && !showEmpty && pdfSource) && (
            <Document
              file={pdfSource}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              onLoadError={(e) => {
                 console.error("PDF Render Error:", e);
                 setLoadingStatus("error");
                 setDebugMsg("ไฟล์ PDF เสียหายหรือรูปแบบไม่ถูกต้อง");
              }}
              loading={<div className="p-10 text-[#7b6c61]">กำลังประมวลผลหน้าเอกสาร...</div>}
            >
              <div
                className={`flex ${rowColClass} items-start`}
                style={{ gap: dual ? pageGap : 0 }}
              >
                {pages.map((p) => (
                  <div
                    key={p}
                    className="inline-block leading-none transition-transform duration-150 ease-out shadow-md" /* แนะนำให้เพิ่ม shadow-md ให้หน้ากระดาษดูมีมิติขึ้นนิดนึงครับ */
                  >
                    <Page
                      pageNumber={p}
                      scale={scale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onLoadSuccess={(info) => {
                        if (!baseW) setBaseW(info.width || 0);
                        if (!baseH) setBaseH(info.height || 0);
                      }}
                    />
                  </div>
                ))}
              </div>
            </Document>
          )}

        </div>
      </div>
    </div>
  );
});

export default InlinePdfSpread;